import { Box, CircularProgress, Divider, Grid, LinearProgress, Link, Stack, Typography } from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Backend, { TaskDetail } from '../backend/Backend';
import './TaskDetailAndStatus.scss';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import { observer } from 'mobx-react-lite';
import { ProcessingDetailLine } from '../common/types';
import processError from '../resources/images/bi_exclamation.svg';
import processDone from '../resources/images/process_done.svg';
import processStepIconDone from '../resources/images/process_step_done.svg';
import processStepIconInProcess from '../resources/images/process_step_inprocess.svg';
import processStepNotStarted from '../resources/images/process_step_not_started.svg';
import configStore, { ConfigStore } from '../stores/ConfigStore';
import FailedValidationsDialog from '../wizard/FailedValidationDialog';
import TimeFrameDates from '../wizard/TimeFrameDates';

interface TaskDetailProps {
    task: TaskDetail;
    configStore: ConfigStore;
}

const TaskDetailAndStatus = observer((props: TaskDetailProps) => {
    let refreshInProgress = false;
    const { t } = useTranslation();
    const { authState, oktaAuth } = useOktaAuth();
    const [progress, setProgress] = useState<number>(0);
    const [processingDetailLines, setProcessingDetailLines] = useState<ProcessingDetailLine[]>([]);
    const [expandedLine, setExpandedLine] = useState<string | false>(false);
    const [processingDone, setProcessingDone] = useState<boolean>(false);
    const [processingError, setProcessingError] = useState<boolean>(false);
    const [task_detail, setTaskDetail] = useState<TaskDetail>(props.task);
    const [show_query, setShowQuery] = useState<string | null>(null);
    const [show_error, setShowError] = useState<string | null>(null);
    const [showFailedValidationsDialog, setShowFailedValidationsDialog] = useState<boolean>(true);
    const [ignoreValidationsErrors, setIgnoreValidationsErrors] = useState<boolean>(false);
    const [descriptionError, setDescriptionError] = useState<string>('');
    const [actionError, setActionError] = useState<Array<string>>([]);
    const [summaryHeader, setSummaryHeader] = useState<string>('');
    const [taskSourceFileName, setTaskSourceFileName] = useState<string>('');
    const handleDetailLineChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
        setExpandedLine(newExpanded ? panel : false);
    };

    useEffect(() => {
        setProcessingDetailLines([]);
        setProcessingDone(false);
        setProcessingError(false);
    }, [ignoreValidationsErrors]);

    useEffect(() => {
        setSummaryHeader(getHeader());
    }, [processingDone, processingError]);

    function refreshStatus() {
        // because sometime the timer timesout after the dialog is closed, the props are going to be empty
        if (refreshInProgress || !configStore.task_id) {
            return;
        }

        refreshInProgress = true;
        fetchStatus().then((statusData: any) => {
            const { lines, actualPercentage } = prepareProcessingDetailLines(statusData);
            setTaskDetail(statusData);
            setProcessingDetailLines(lines);
            setProgress(actualPercentage);

            setPartialDescriptionHeader(lines);

            const pd = statusData.task_status === 'Done';
            const pe = statusData.task_status === 'Error' || statusData.task_status === 'Warning';
            setProcessingDone(pd);
            setProcessingError(pe);

            const canRefresh = !pd && !pe && document.getElementsByClassName('abcd1234').length > 0;

            if (canRefresh === true) {
                refreshInProgress = false;
                setTimeout(() => {
                    refreshStatus();
                }, 1000 * 10);
            } else {
                console.log('TaskDetailAndStatus refresh disabled or skipped');
            }
        });
    }

    const prepareProcessingDetailLines = (statusData) => {
        const lines: ProcessingDetailLine[] = [];
        let totalPercentage = 0;
        let actualPercentage = 0;

        if (statusData && statusData.task_steps_completed) {
            const statusDataLines = JSON.parse(statusData.task_steps_completed);
            const taskData = statusData.task_data ? JSON.parse(statusData.task_data) : '';
            let prevLine = undefined;

            if (statusData.task_source_path) {
                const sourcePathSplit = statusData.task_source_path.split('/');
                const sourceFileName = sourcePathSplit[sourcePathSplit.length - 1];
                setTaskSourceFileName(sourceFileName.substring(0, sourceFileName.length - 2));
            }

            for (let i = 0; i < statusDataLines.length; i++) {
                const line = statusDataLines[i];
                const lineGotError: boolean =
                    line.name === 'running-mapping'
                        ? line.substeps &&
                          Object.values(line.substeps).find((substep) =>
                              Object.values(substep).find((sub) => sub.status === 'failed'),
                          ) !== undefined
                        : line.status === 'failed' && line.progress > 0;
                const icon =
                    line.progress === 0 || (prevLine && prevLine.status === 'error')
                        ? processStepNotStarted
                        : lineGotError
                        ? processError
                        : line.progress >= line.total
                        ? processStepIconDone
                        : processStepIconInProcess;
                const iconCls = icon === processStepIconInProcess ? 'IconInProcess' : 'Icon';

                lines.push({
                    lineType: line.name,
                    progress: line.progress > line.total ? line.total : line.progress,
                    total: line.total,
                    title: t(`summary.${line.name}`),
                    icon,
                    iconCls,
                    substeps: line.substeps,
                    error: lineGotError,
                    status: line.status || undefined,
                    processorOutputUrl: line.output_url || undefined,
                    messages: line.messages ? JSON.parse(line.messages) : [],
                    ignore_failed_validation: line.ignore_failed_validation,
                    task_data: taskData,
                });

                actualPercentage += line.progress;
                totalPercentage += line.total;
                prevLine = line;
            }
        } else {
            totalPercentage = 100;
        }

        return { lines, actualPercentage: (actualPercentage / totalPercentage) * 100 };
    };

    async function fetchStatus() {
        const td = await Backend.getTaskDetail(configStore.task_id, oktaAuth);
        return td.data;
    }

    async function handleDownloadResults(event) {
        event.stopPropagation(); // prevent accordion expand
        const results_id: string = task_detail.task_results;

        await Backend.downloadResultsViaUrl(results_id, oktaAuth);
    }

    async function handleProcessorDownload(event) {
        event.stopPropagation(); // prevent accordion expand
        const results_id: string = task_detail.task_results;

        await Backend.downloadProcessorUrl(results_id, oktaAuth);
    }

    async function handleDownload(event, link: string) {
        event.stopPropagation(); // prevent accordion expand

        await Backend.downloadUrl(link, oktaAuth);
    }

    const setPartialDescriptionHeader = (lines: ProcessingDetailLine[]) => {
        const partialLine = lines.find((line) => line.lineType === 'partial-processing');
        const processLine = lines.find((line) => line.lineType === 'processing-data');
        if (partialLine) {
            if (partialLine.error || partialLine.status === 'failed') {
                setDescriptionError(t('summary.partial-error.concat-error-desc'));
                setActionError(t('summary.partial-error.concat-error-actions', { returnObjects: true }));
                setSummaryHeader(t('summary.partial-error.concat-error-header'));
            } else if (processLine.error || processLine.status === 'failed') {
                setDescriptionError(t('summary.partial-error.process-error-desc'));
                setActionError(t('summary.partial-error.process-error-actions', { returnObjects: true }));
                setSummaryHeader(t('summary.partial-error.process-error-header'));
            } else {
                setDescriptionError('');
                setActionError([]);
                setSummaryHeader(getHeader());
            }
        }
    };

    const getHeader = () =>
        processingDone
            ? t('summary.processing-done')
            : processingError
            ? t('summary.processing-error')
            : t('summary.processing');

    useEffect(() => {
        if (processingDetailLines.length > 0 || processingDone || processingError) {
            return;
        }

        // if (!notificationSocket){
        //     NotificationService.createWebSocket(oktaAuth.getAccessToken())
        // }

        refreshStatus();
    }, []);

    return (
        <div className="abcd1234">
            <Stack direction="column" className="TaskDetailAndStatus">
                <Stack direction="column" className="Content">
                    <Stack direction="row" className="SummaryStatus">
                        {processingDone ? <img className="Processing-Done" alt="" src={processDone} /> : null}
                        {processingError ? <img className="Processing-Error" alt="" src={processError} /> : null}
                        {!processingDone && !processingError && (
                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                <CircularProgress
                                    data-testid="CircularProgress"
                                    variant="determinate"
                                    value={progress}
                                    classes={{ circle: 'Indicator' }}
                                />

                                <Box
                                    sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography variant="caption" component="div" color="text.secondary">{`${Math.round(
                                        progress,
                                    )}%`}</Typography>
                                </Box>
                            </Box>
                        )}
                        <Typography className="Description">{summaryHeader}</Typography>
                        {taskSourceFileName !== '' && (
                            <Stack className="SourceFileNameStack">
                                <Typography className="SourceFileNameTitle">File name:</Typography>
                                <Typography>{taskSourceFileName}</Typography>
                            </Stack>
                        )}
                    </Stack>
                    <Divider className="Divider" />
                    {descriptionError ? (
                        <Typography>
                            {descriptionError}
                            <br />
                            <br />
                            <b>What should you do now?</b>
                            <br />
                            <br />
                            {actionError.map((item, index) => (
                                <li key={index}>{item}</li>
                            ))}
                        </Typography>
                    ) : null}
                    <Stack direction="column" className="ProcessingDetails">
                        <Stack direction="row" justifyContent="space-between">
                            <Typography className="Description">{configStore.config_name}</Typography>
                            {configStore.startDate && configStore.endDate ? (
                                <Stack direction="row" className="ConfigTimeframe">
                                    <Typography display="flex" className="ConfigTimeframeText" marginRight="7px">
                                        Dataset time frame:
                                    </Typography>
                                    <TimeFrameDates from={configStore.startDate} to={configStore.endDate} />
                                </Stack>
                            ) : null}
                        </Stack>
                        <Stack className="ProcessingDetailLines">
                            {processingDetailLines.map((line, index) => (
                                <Stack key={index}>
                                    <Accordion
                                        key={index}
                                        expanded={!!line.substeps && line.lineType === 'running-mapping'}
                                        onChange={handleDetailLineChange(line.lineType)}
                                    >
                                        <AccordionSummary classes={{ content: 'Summary' }}>
                                            <img
                                                className={line.iconCls ? line.iconCls : 'Icon'}
                                                alt=""
                                                src={line.icon}
                                            />
                                            {line.lineType === 'validating-data' &&
                                                line.status === 'error' &&
                                                !line.ignore_failed_validation && (
                                                    <FailedValidationsDialog
                                                        setIgnoreValidationsErrors={setIgnoreValidationsErrors}
                                                        showFailedValidationsDialog={showFailedValidationsDialog}
                                                        setShowFailedValidationsDialog={setShowFailedValidationsDialog}
                                                        configStore={props.configStore}
                                                        taskId={task_detail.rowid}
                                                        validationProgress={line}
                                                        handleDownload={handleDownload}
                                                    />
                                                )}
                                            <Typography className="LineTitle">{line.title}</Typography>
                                            <LinearProgress
                                                className="LineProgress"
                                                classes={{
                                                    root: 'LineProgressBarRoot',
                                                    bar: line.error ? 'LineProgressBarFail' : 'LineProgressBar',
                                                }}
                                                variant="determinate"
                                                value={(line.progress / line.total) * 100}
                                            />
                                            <Typography className="LineProgressLabel">
                                                {Math.round((line.progress / line.total) * 100)} %
                                            </Typography>
                                            {line.lineType === 'creating-archive' && line.processorOutputUrl ? (
                                                <Link
                                                    className="DownloadLink"
                                                    href="#"
                                                    underline="hover"
                                                    onClick={(event) => {
                                                        handleDownload(event, line.processorOutputUrl);
                                                    }}
                                                    data-testid="summary-download-archive-btn"
                                                >
                                                    {t('common.download-archive')}
                                                </Link>
                                            ) : null}
                                            {line.lineType === 'partial-processing' && line.processorOutputUrl ? (
                                                <Link
                                                    className="DownloadLink"
                                                    href="#"
                                                    underline="hover"
                                                    onClick={(event) => {
                                                        handleDownload(event, line.processorOutputUrl);
                                                    }}
                                                    data-testid="summary-download-partial-btn"
                                                >
                                                    {t('summary.download-partial-output')}
                                                </Link>
                                            ) : null}
                                            {line.lineType === 'processing-data' && line.processorOutputUrl ? (
                                                <Link
                                                    className="DownloadLink"
                                                    href="#"
                                                    underline="hover"
                                                    onClick={(event) => {
                                                        handleDownload(event, line.processorOutputUrl);
                                                    }}
                                                    data-testid="summary-download-processor-output-btn"
                                                >
                                                    {t('summary.download-processor-output')}
                                                </Link>
                                            ) : null}
                                            {line.lineType === 'validating-data' && line.processorOutputUrl ? (
                                                <Link
                                                    className="DownloadLink"
                                                    href="#"
                                                    underline="hover"
                                                    onClick={(event) => {
                                                        handleDownload(event, line.processorOutputUrl);
                                                    }}
                                                    data-testid="summary-download-validation-output-btn"
                                                >
                                                    {t('summary.download-validation-output')}
                                                </Link>
                                            ) : null}
                                            {line.lineType === 'injecting-data' &&
                                            line.task_data?.process_info?.inject_data?.app_name ? (
                                                <Typography className="DownloadLink">
                                                    <span className="InnerTitle">SF app name:</span>{' '}
                                                    {line.task_data?.process_info?.inject_data?.app_name}
                                                </Typography>
                                            ) : null}
                                            {line.error && task_detail.task_results ? (
                                                <Link
                                                    className="DownloadLink"
                                                    href="#"
                                                    underline="hover"
                                                    onClick={() =>
                                                        setShowError(
                                                            show_error === line.lineType ? null : line.lineType,
                                                        )
                                                    }
                                                >
                                                    {show_error === line.lineType
                                                        ? t('summary.hide-error')
                                                        : t('summary.show-error')}
                                                </Link>
                                            ) : null}
                                            {show_error === line.lineType && (
                                                <Stack className="InfoPaper">
                                                    <Typography variant="body1" color="red" paddingLeft={2}>
                                                        Error: {task_detail.task_results}
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {line.substeps && line.lineType === 'running-mapping' ? (
                                                <Grid container spacing={2}>
                                                    <Grid item xs={2} />
                                                    <Grid item xs={2} textAlign="center">
                                                        <Typography>{t('summary.running-queries')}</Typography>
                                                    </Grid>
                                                    <Grid item xs={2} textAlign="center">
                                                        <Typography>{t('summary.mapping-columns')}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} />
                                                    {line.substeps && line.substeps.queries
                                                        ? Object.keys(line.substeps.queries).map((object, index) => (
                                                              <Fragment key={index}>
                                                                  <Grid item xs={2} display="flex" alignItems="center">
                                                                      <Typography paddingLeft={2}>{object}</Typography>
                                                                  </Grid>
                                                                  {/* Running Queries */}
                                                                  <Grid
                                                                      item
                                                                      xs={2}
                                                                      textAlign="center"
                                                                      display="flex"
                                                                      flexDirection="column"
                                                                  >
                                                                      <div
                                                                          style={{
                                                                              display: 'flex',
                                                                              alignItems: 'center',
                                                                              justifyContent: 'center',
                                                                              height: '32px',
                                                                          }}
                                                                      >
                                                                          {line.substeps.queries[object].status ===
                                                                              'loading' && (
                                                                              <img
                                                                                  className="IconInProcess"
                                                                                  style={{
                                                                                      maxWidth: '32px',
                                                                                      maxHeight: '32px',
                                                                                  }}
                                                                                  alt=""
                                                                                  src={processStepIconInProcess}
                                                                              />
                                                                          )}
                                                                          {line.substeps.queries[object].status ===
                                                                              'success' && (
                                                                              <img
                                                                                  className="Icon"
                                                                                  alt=""
                                                                                  src={processStepIconDone}
                                                                              />
                                                                          )}
                                                                          {line.substeps.queries[object].status ===
                                                                              'failed' && (
                                                                              <img
                                                                                  className="IconInProcess"
                                                                                  alt=""
                                                                                  src={processError}
                                                                              />
                                                                          )}
                                                                      </div>
                                                                  </Grid>
                                                                  {/* Mapping Columns */}
                                                                  <Grid
                                                                      item
                                                                      xs={2}
                                                                      textAlign="center"
                                                                      display="flex"
                                                                      flexDirection="column"
                                                                  >
                                                                      {line.substeps.mapping_columns &&
                                                                      line.substeps.mapping_columns[object] ? (
                                                                          <div
                                                                              style={{
                                                                                  display: 'flex',
                                                                                  alignItems: 'center',
                                                                                  justifyContent: 'center',
                                                                                  height: '32px',
                                                                              }}
                                                                          >
                                                                              {line.substeps.mapping_columns[object]
                                                                                  .status === 'loading' && (
                                                                                  <img
                                                                                      className="IconInProcess"
                                                                                      style={{ maxWidth: '32px' }}
                                                                                      alt=""
                                                                                      src={processStepIconInProcess}
                                                                                  />
                                                                              )}
                                                                              {line.substeps.mapping_columns[object]
                                                                                  .status === 'success' && (
                                                                                  <img
                                                                                      className="Icon"
                                                                                      alt=""
                                                                                      src={processStepIconDone}
                                                                                  />
                                                                              )}
                                                                              {line.substeps.mapping_columns[object]
                                                                                  .status === 'failed' && (
                                                                                  <img
                                                                                      className="IconInProcess"
                                                                                      alt=""
                                                                                      src={processError}
                                                                                  />
                                                                              )}
                                                                          </div>
                                                                      ) : null}
                                                                      {line.substeps.mapping_columns &&
                                                                      line.substeps.mapping_columns[object] &&
                                                                      line.substeps.mapping_columns[object].status ===
                                                                          'loading' &&
                                                                      line.substeps.mapping_columns[object]
                                                                          .current_column ? (
                                                                          <Typography>
                                                                              {t('summary.current-column')}:{' '}
                                                                              {
                                                                                  line.substeps.mapping_columns[object]
                                                                                      .current_column
                                                                              }
                                                                          </Typography>
                                                                      ) : null}
                                                                      {line.substeps.mapping_columns &&
                                                                      line.substeps.mapping_columns[object] &&
                                                                      line.substeps.mapping_columns[object].status ===
                                                                          'failed' &&
                                                                      line.substeps.mapping_columns[object]
                                                                          .current_column ? (
                                                                          <Typography>
                                                                              {t('summary.error-in-column')}:{' '}
                                                                              {
                                                                                  line.substeps.mapping_columns[object]
                                                                                      .current_column
                                                                              }
                                                                          </Typography>
                                                                      ) : null}
                                                                  </Grid>
                                                                  {/* Show/Hide Query */}
                                                                  {line.substeps.queries[object].query ? (
                                                                      <Grid
                                                                          item
                                                                          xs={6}
                                                                          display="flex"
                                                                          alignItems="center"
                                                                      >
                                                                          <Link
                                                                              className="DownloadLink"
                                                                              href="#"
                                                                              underline="hover"
                                                                              onClick={() =>
                                                                                  setShowQuery(
                                                                                      show_query === object
                                                                                          ? null
                                                                                          : object,
                                                                                  )
                                                                              }
                                                                          >
                                                                              {show_query === object
                                                                                  ? t('summary.hide-query')
                                                                                  : t('summary.show-query')}
                                                                          </Link>
                                                                      </Grid>
                                                                  ) : null}
                                                                  {show_query === object && (
                                                                      <Grid item xs={12}>
                                                                          <Stack className="InfoPaper">
                                                                              <Typography
                                                                                  style={{ wordWrap: 'break-word' }}
                                                                              >
                                                                                  {line.substeps.queries[object].query}
                                                                              </Typography>
                                                                          </Stack>
                                                                      </Grid>
                                                                  )}
                                                              </Fragment>
                                                          ))
                                                        : null}
                                                </Grid>
                                            ) : null}
                                        </AccordionDetails>
                                    </Accordion>
                                </Stack>
                            ))}
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </div>
    );
});

export default TaskDetailAndStatus;
